import React from 'react';
import { Formik, FieldArray, useFormikContext } from 'formik';
import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import snakecaseKeys from 'snakecase-keys';
import { NoteInternalAssessment, patchSessionPsychiatryNote } from '@uplift/api';
import { useAppointmentByIdQuery, useBatchAssessmentsSubmissions, useSessionPsychiatryNoteQuery, useSessionQuery } from '@uplift/queries';
import { ServerFormSection } from '../../../ServerForm/ServerFormSection.component';
import { PsychiatryNoteSection } from '../components/PsychiatryNoteSection.component';
import { NumberInput, TextInput, YesNoRadioButtonGroup } from '../../../Inputs';
import { Button } from '../../../Button';
import { FormikAutoSave } from '../../../ServerForm';
import { mapAssessmentScoreToDescription } from '@uplift/utils';
import { useL10n } from '@uplift/hooks';
import { FloatingActionBar } from '../components/FloatingActionBar.component';
import { Markdown } from '../../..//Markdown';
import { useQueryClient } from '@tanstack/react-query';
import { AssessmentKeys } from '@uplift/types';
import { Chip } from '../../../Chip';


const useStyles = makeStyles()(theme => ({
  assessmentChip: {
    backgroundColor: theme.palette.grey[200],
  }
}));


export const InternalAssessmentsSectionForm: React.VFC<{
  patientId: string;
  assessments: NoteInternalAssessment[];
  namespace?: string;
}> = ({ patientId, assessments, namespace = 'internal' }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const submissionIds = assessments.map(i => i.submissionId);
  const { data } = useBatchAssessmentsSubmissions(patientId, submissionIds, { suspense: true });

  if (assessments?.length) {
    return (
      <Box display="flex" flexDirection="column" gap="24px">
        {assessments.map(({ submissionId }, index) => {
          const submission = data?.submissionById?.[submissionId];

          if (!submission) {
            return null;
          }

          const form = data?.formById?.[submission?.submission?.formId];

          if (!form) {
            return null;
          }

          const assessmentKey = data.formIdToKey[form.formId];

          if (!assessmentKey) {
            return null;
          }

          const scoreToDescription = mapAssessmentScoreToDescription(data.formIdToKey[form.formId], l10n);
          const descriptionTitles: Partial<Record<AssessmentKeys, string>> = l10n.clientProfile.assessments.assessmentDescriptionTitle;
          const descriptionTitle = assessmentKey in descriptionTitles
            ? descriptionTitles[assessmentKey]
            : undefined;

          return (
            <Box key={submissionId} display="flex" flexDirection="column" gap="15px" alignItems="flex-start">
              {descriptionTitle && <Typography style={{ fontWeight: 450 }}>{descriptionTitle}</Typography>}
              <Chip label={`${form.name} | ${submission.score} ${scoreToDescription(submission.score) ?? ''}`} square className={classes.assessmentChip} />
              <YesNoRadioButtonGroup asBoolean name={`${namespace}.${index}.isReviewed`} label={`Did you review the ${form.name} results?`} />
            </Box>
          );
        })}
      </Box>
    );
  }

  return null;
};


export const AssessmentSectionForm: React.VFC<{ sessionId: string }> = ({ sessionId }) => {
  const formik = useFormikContext<{
    internal: NoteInternalAssessment[];
    external: { name: string; result: number; id: string }[];
  }>();
  const { data: session } = useSessionQuery(sessionId, { suspense: true });
  const { data: appointment } = useAppointmentByIdQuery(session?.appointmentId ?? '', { suspense: true });

  return (
    <ServerFormSection>
      {formik.values.internal?.length && appointment?.patientId ? (
        <>
          <InternalAssessmentsSectionForm namespace="internal" patientId={appointment?.patientId} assessments={formik.values.internal} />

          <Box>
            <Divider />
          </Box>
        </>
      ) : null}

      <FieldArray
        name="external"
        render={helpers => (
          <>
            <Box display="flex" flexDirection="column" gap="24px">
              {formik.values.external.map((assessment, index) => (
                <Box display="flex" key={assessment.id}>
                  <Box p={3} flex={1} bgcolor="grey.100" display="flex" flexDirection="column" gap="16px">
                    <TextInput label="Assessment Name" name={`${helpers.name}.${index}.name`} color="secondary" />
                    <NumberInput label="Result" name={`${helpers.name}.${index}.result`} color="secondary" />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <IconButton onClick={() => helpers.remove(index)}><DeleteOutline /></IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box>
              <Button
                startIcon={<Add />}
                onClick={() => {
                  helpers.push({
                    id: Date.now(),
                    name: '',
                    result: '',
                  });
                }}
              >Add assessment</Button>
            </Box>
          </>
        )}
      />

      <TextInput name="additionalInformation" label="Additional Information (optional)" color="secondary" multiline minRows={1} />
    </ServerFormSection>
  );
};


export const AssessmentSectionFormik: React.FC<{ sessionId: string; autoSave?: boolean;  onSuccess?: () => void }> = ({ sessionId, autoSave, onSuccess, children }) => {
  const queryClient = useQueryClient();
  const { data } = useSessionPsychiatryNoteQuery(sessionId, { suspense: true });
  const { data: session } = useSessionQuery(sessionId, { suspense: true });

  const initialValues = React.useMemo(() => {
    if (!data?.assessments) {
      return { additionalInformation: null, internal: [], external: [] };
    }

    return {
      additionalInformation: data.assessments.additionalInformation,
      internal: data.assessments.internal,
      external: data.assessments.external.map((e, index) => ({ ...e, id: index })),
    };
  }, [ data?.assessments ]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values) => {
        await patchSessionPsychiatryNote(sessionId, {
          assessments: {
            additional_information: values.additionalInformation ?? undefined,
            internal: snakecaseKeys(values.internal),
            external: snakecaseKeys(values.external.map(e => ({ name: e.name, result: e.result }))),
          },
        });
        await queryClient.invalidateQueries([ 'session', sessionId ]);
        await queryClient.invalidateQueries([ 'appointment', session?.appointmentId, 'session' ]);
        onSuccess?.();
      }}
    >
      <>
        {children}
        {autoSave ? <FormikAutoSave /> : null}
      </>
    </Formik>
  );
};


export const AssessmentViewSection: React.VFC<{ sessionId: string; onEdit?: () => void }> = ({ sessionId, onEdit }) => {
  const l10n = useL10n();
  const { data: psychiatryNote } = useSessionPsychiatryNoteQuery(sessionId, { suspense: true });
  const { data: session } = useSessionQuery(sessionId, { suspense: true });
  const { data: appointment } = useAppointmentByIdQuery(session?.appointmentId ?? '', { suspense: true });
  const submissionIds = psychiatryNote?.assessments.internal.map(i => i.submissionId);
  const { data: batchSubmissions } = useBatchAssessmentsSubmissions(appointment?.patientId, submissionIds);

  return (
    <PsychiatryNoteSection
      title="Assessment"
      actions={onEdit && (
        <Button
          onClick={onEdit}
          size="small"
          color="secondary"
        >
          Edit
        </Button>
      )}
    >
      <Box px={3}>
        {psychiatryNote?.assessments.internal.map(i => {
          const submission = batchSubmissions?.submissionById[i.submissionId];

          if (!submission) return null;

          const form = batchSubmissions?.formById[submission?.submission.formId];

          if (!form) return null;

          const assessmentKey = batchSubmissions?.formIdToKey[submission?.submission.formId];
          const scoreToDescription = mapAssessmentScoreToDescription(assessmentKey, l10n);

          return (
            <>
              <Typography key={i.submissionId}>
                Client scored <strong>{submission.score} on a {form.name}</strong> suggesting {scoreToDescription(submission.score)?.toLowerCase()} symptoms. Provider {i.isReviewed ? 'reviewed' : 'did not review'} responses.
              </Typography>
              <br />
            </>
          );
        })}
      </Box>

      <Box px={3}>
        {psychiatryNote?.assessments.external.map((i, index, arr) => {
          return (
            <>
              <Typography key={i.name + i.result}>
                Provider administered <strong>{i.name}</strong> during session. Results: {i.result}
              </Typography>
              {index === arr.length - 1 ? null : <br />}
            </>
          );
        })}
      </Box>

      {psychiatryNote?.assessments.additionalInformation && (
        <Box px={3}>
          <Markdown>{`**Additional Information**: ${psychiatryNote?.assessments.additionalInformation}`}</Markdown>
        </Box>
      )}
    </PsychiatryNoteSection>
  );
};



export const AssessmentEditSection: React.VFC<{ sessionId: string; autoSave?: boolean }> = ({ sessionId, autoSave }) => {
  return (
    <PsychiatryNoteSection title="Assessment">
      <AssessmentSectionFormik autoSave={autoSave} sessionId={sessionId}>
        <AssessmentSectionForm sessionId={sessionId} />
      </AssessmentSectionFormik>
    </PsychiatryNoteSection>
  );
};


export const AssessmentSection: React.VFC<{ sessionId: string }> = ({ sessionId }) => {
  const [ isEditing, setIsEditing ] = React.useState(false);

  if (isEditing) {
    return (
      <AssessmentSectionFormik sessionId={sessionId} onSuccess={() => setIsEditing(false)}>
        <>
          <PsychiatryNoteSection title="Assessment">
            <AssessmentSectionForm sessionId={sessionId} />
          </PsychiatryNoteSection>
          <FloatingActionBar onCancel={() => setIsEditing(false)} />
        </>
      </AssessmentSectionFormik>
    );
  }

  return (
    <AssessmentViewSection sessionId={sessionId} onEdit={() => setIsEditing(true)} />
  );
};
